// The titles × stations install matrix (F-CAT-03, spec/13 §9.2): rows are the venue's library titles, columns its
// stations grouped by class, and each cell what that station last reported — installed (✓, the version on hover),
// updating (its percent), missing (–), or not reported (the station has never scanned). A title not offered to a
// station's class has no cell there. Headers stick while scrolling; a class filter, a zone filter and "missing only"
// narrow it, and "Rescan all" asks every station to scan now (`station.library.rescan`, `stations:manage`). Each cell
// carries its version and size on disk (GB) from the station's latest report and that report's time. One read:
// `GET …/library/installs`.
//
// Apps installed for players (F-CAT-09): where such an app is not installed, the cell says what the PC reported instead —
// installing (↓), could not (×, with why on hover), installed for another Windows account only (○), not in a diskless
// image, or a Store app on no account of the PC — and the title carries a "For players" badge when the venue turned it on.

import type { AppProvisioningFailure, AppProvisioningState, LibraryInstallMatrix } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, EmptyState, SelectField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { CardsSkeleton } from '../../ui';
import { useInstallMatrix, useRescanInstalls } from './queries';
import { useProblem } from '../../api/use-problem';

type Station = LibraryInstallMatrix['stations'][number];
type Title = LibraryInstallMatrix['titles'][number];
type Cell = LibraryInstallMatrix['cells'][number];
type Provisioning = LibraryInstallMatrix['provisioning'][number];

/** What one cell shows. */
export type MatrixCellState =
  | { readonly kind: 'installed'; readonly version: string | null; readonly sizeBytes: number | null; readonly reportedAt: string | null }
  | { readonly kind: 'unverified' }
  | { readonly kind: 'updating'; readonly progress: number; readonly version: string | null; readonly sizeBytes: number | null; readonly reportedAt: string | null }
  | { readonly kind: 'missing' }
  | {
      readonly kind: 'players';
      readonly state: AppProvisioningState;
      readonly failure: AppProvisioningFailure | null;
      readonly detail: string | null;
      readonly nextAttemptAt: string | null;
    }
  | { readonly kind: 'not-reported' }
  | { readonly kind: 'not-offered' };

/**
 * A cell's state from the matrix (pure): offered to the station's class, reported, and installed or not — and, for an app
 * not installed, what the PC said about installing it for players (F-CAT-09).
 */
export function cellState(title: Title, station: Station, cell: Cell | undefined, provisioning?: Provisioning): MatrixCellState {
  if (title.classIds.length > 0 && !title.classIds.includes(station.classId)) return { kind: 'not-offered' };
  if (station.scannedAt === null) return { kind: 'not-reported' };
  if (cell === undefined) {
    return provisioning === undefined || provisioning.state === 'installed'
      ? { kind: 'missing' }
      : { kind: 'players', state: provisioning.state, failure: provisioning.failure, detail: provisioning.detail, nextAttemptAt: provisioning.nextAttemptAt };
  }
  // The PC could not check it (F-CAT-03): shown as a question, never as a tick.
  if (cell.unverified) return { kind: 'unverified' };
  const sizeBytes = cell.sizeBytes ?? null;
  return cell.updating === null
    ? { kind: 'installed', version: cell.version, sizeBytes, reportedAt: station.scannedAt }
    : { kind: 'updating', progress: Math.round(cell.updating.progress), version: cell.version, sizeBytes, reportedAt: station.scannedAt };
}

/** A size on disk in GB (GiB, as the launchers show it), one decimal. Display only — sizes are bytes on the wire. */
export function gigabytes(bytes: number): string {
  return (bytes / 1024 ** 3).toFixed(1);
}

export const ALL = 'all';
/** The zone filter's value for stations standing in no zone. */
export const NO_ZONE = 'none';

/** The stations the class and zone filters keep (pure): `ALL` keeps every one; `NO_ZONE` those in no zone. */
export function stationsInView(stations: readonly Station[], classId: string, zoneId: string): Station[] {
  return stations.filter(
    (station) =>
      (classId === ALL || station.classId === classId) &&
      (zoneId === ALL || (zoneId === NO_ZONE ? station.zoneId === null : station.zoneId === zoneId)),
  );
}

export function InstallMatrix({
  organizationId,
  venueId,
  canRescan,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canRescan: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const matrix = useInstallMatrix(organizationId, venueId);
  const rescan = useRescanInstalls(organizationId, venueId);
  const [classId, setClassId] = useState<string>(ALL);
  const [zoneId, setZoneId] = useState<string>(ALL);
  const [missingOnly, setMissingOnly] = useState(false);

  const view = useMemo(() => {
    const data = matrix.data;
    if (data === undefined) return undefined;
    const cells = new Map(data.cells.map((cell) => [`${cell.entryId}|${cell.stationId}`, cell]));
    const provisioning = new Map(data.provisioning.map((cell) => [`${cell.entryId}|${cell.stationId}`, cell]));
    const stations = stationsInView(data.stations, classId, zoneId);
    const groups = data.classes
      .map((klass) => ({ klass, stations: stations.filter((station) => station.classId === klass.classId) }))
      .filter((group) => group.stations.length > 0);
    const rows = data.titles
      .map((title) => ({
        title,
        states: stations.map((station) => cellState(title, station, cells.get(`${title.entryId}|${station.stationId}`), provisioning.get(`${title.entryId}|${station.stationId}`))),
      }))
      .filter((row) => !missingOnly || row.states.some((state) => state.kind === 'missing' || state.kind === 'players'));
    return { data, stations, groups, rows };
  }, [matrix.data, classId, zoneId, missingOnly]);

  if (view === undefined) {
    return matrix.isError ? (
      <LoadFailure error={matrix.error} message={t('library.matrix.loadFailed')} onRetry={() => void matrix.refetch()} />
    ) : (
      <CardsSkeleton label={t('library.matrix.loading')} />
    );
  }

  const rescanAll = () =>
    rescan.mutate(
      {},
      {
        onSuccess: (issued) => show({ tone: 'ok', title: t('library.matrix.rescanSent', { count: issued.commands.length }) }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.matrix.rescanFailed') }) }),
      },
    );

  const toolbar = (
    <div className="console-install-matrix__toolbar">
      <SelectField
        label={t('library.matrix.class')}
        value={classId}
        onValueChange={setClassId}
        options={[{ value: ALL, label: t('library.matrix.allClasses') }, ...view.data.classes.map((klass) => ({ value: klass.classId, label: klass.name }))]}
      />
      {view.data.zones.length === 0 ? null : (
        <SelectField
          label={t('library.matrix.zone')}
          value={zoneId}
          onValueChange={setZoneId}
          options={[
            { value: ALL, label: t('library.matrix.allZones') },
            ...view.data.zones.map((zone) => ({ value: zone.zoneId, label: zone.name })),
            { value: NO_ZONE, label: t('library.matrix.noZone') },
          ]}
        />
      )}
      <Checkbox label={t('library.matrix.missingOnly')} checked={missingOnly} onCheckedChange={(checked) => setMissingOnly(checked === true)} />
      {canRescan ? (
        <Button variant="secondary" icon={<Icon name="refresh" />} busy={rescan.isPending} onClick={rescanAll}>
          {t('library.matrix.rescanAll')}
        </Button>
      ) : null}
      {view.data.titles.some((title) => title.provisionable) ? <p className="console-muted console-install-matrix__legend">{t('library.matrix.legend')}</p> : null}
    </div>
  );

  if (view.data.titles.length === 0 || view.data.stations.length === 0) {
    return (
      <>
        {toolbar}
        <EmptyState icon={<NavIcon name="library" />} title={t('library.matrix.empty.title')} description={t('library.matrix.empty.body')} />
      </>
    );
  }

  return (
    <>
      {toolbar}
      {/* A scrolling region must be reachable by keyboard (axe scrollable-region-focusable). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className="console-install-matrix" role="region" aria-label={t('library.matrix.caption')} tabIndex={0}>
        <table className="console-install-matrix__table" aria-label={t('library.matrix.caption')}>
          <thead>
            <tr>
              <th scope="col" rowSpan={2} className="console-install-matrix__corner">
                {t('library.matrix.title')}
              </th>
              {view.groups.map((group) => (
                <th key={group.klass.classId} scope="colgroup" colSpan={group.stations.length}>
                  {group.klass.name}
                </th>
              ))}
            </tr>
            <tr>
              {view.groups.flatMap((group) =>
                group.stations.map((station) => (
                  <th
                    key={station.stationId}
                    scope="col"
                    title={
                      station.scannedAt === null
                        ? t('library.matrix.neverReported')
                        : t('library.matrix.scannedAt', { time: new Date(station.scannedAt).toLocaleString() })
                    }
                  >
                    <span className="console-num">{station.name ?? t('floor.tile.numbered', { number: station.number })}</span>
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {view.rows.length === 0 ? (
              <tr>
                <td colSpan={view.stations.length + 1} className="console-muted">
                  {t('library.matrix.noneMissing')}
                </td>
              </tr>
            ) : (
              view.rows.map((row) => (
                <tr key={row.title.entryId}>
                  <th scope="row">
                    {row.title.name}
                    {row.title.installForPlayers ? (
                      <>
                        {' '}
                        <Badge tone="info">{t('library.players.label')}</Badge>
                      </>
                    ) : null}
                  </th>
                  {view.groups
                    .flatMap((group) => group.stations)
                    .map((station) => {
                      const state = row.states[view.stations.indexOf(station)]!;
                      return <MatrixCell key={station.stationId} state={state} />;
                    })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MatrixCell({ state }: { readonly state: MatrixCellState }): ReactNode {
  const { t } = useTranslation('console');
  /** The cell's detail on hover: version · size · when the station reported it (F-CAT-03). */
  const detailOf = (version: string | null, sizeBytes: number | null, reportedAt: string | null) =>
    [
      version,
      sizeBytes === null ? null : t('library.matrix.sizeGb', { size: gigabytes(sizeBytes) }),
      reportedAt === null ? null : t('library.matrix.reportedAt', { time: new Date(reportedAt).toLocaleString() }),
    ]
      .filter((part): part is string => part !== null)
      .join(' · ') || undefined;
  switch (state.kind) {
    case 'installed':
      return (
        <td
          className="console-install-matrix__cell is-installed"
          title={detailOf(state.version, state.sizeBytes, state.reportedAt)}
          aria-label={[t('library.matrix.cell.installed', { version: state.version ?? '' }), detailOf(null, state.sizeBytes, state.reportedAt)].filter(Boolean).join(', ')}
        >
          <span aria-hidden="true">✓</span>
          {state.version === null ? null : <span className="console-install-matrix__version">{state.version}</span>}
          {state.sizeBytes === null ? null : <span className="console-install-matrix__size">{t('library.matrix.sizeGb', { size: gigabytes(state.sizeBytes) })}</span>}
        </td>
      );
    case 'updating':
      return (
        <td
          className="console-install-matrix__cell is-updating"
          title={detailOf(state.version, state.sizeBytes, state.reportedAt)}
          aria-label={t('library.matrix.cell.updating', { percent: state.progress })}
        >
          <span className="console-num">{state.progress}%</span>
        </td>
      );
    case 'missing':
      return (
        <td className="console-install-matrix__cell is-missing" aria-label={t('library.matrix.cell.missing')}>
          <span aria-hidden="true">–</span>
        </td>
      );
    case 'players': {
      // What the PC said about installing the app for players: the state, why it failed, when it tries again.
      const said = [
        t(`library.players.state.${state.state}`),
        state.failure === null ? null : t(`library.players.failure.${state.failure}`),
        state.detail === null ? null : t('library.players.detail', { detail: state.detail }),
        state.nextAttemptAt === null ? null : t('library.players.retry', { time: new Date(state.nextAttemptAt).toLocaleString() }),
      ]
        .filter((part): part is string => part !== null)
        .join(' ');
      const glyph = state.state === 'installing' ? '↓' : state.state === 'failed' ? '×' : state.state === 'other_account_only' ? '○' : '–';
      return (
        <td className={`console-install-matrix__cell is-players is-${state.state.replaceAll('_', '-')}`} title={said} aria-label={said}>
          <span aria-hidden="true">{glyph}</span>
        </td>
      );
    }
    case 'unverified':
      return (
        <td className="console-install-matrix__cell is-unknown" title={t('library.matrix.cell.unverified')} aria-label={t('library.matrix.cell.unverified')}>
          <span aria-hidden="true">?</span>
        </td>
      );
    case 'not-reported':
      return (
        <td className="console-install-matrix__cell is-unknown" aria-label={t('library.matrix.cell.notReported')}>
          <span aria-hidden="true">?</span>
        </td>
      );
    case 'not-offered':
      return <td className="console-install-matrix__cell is-off" aria-label={t('library.matrix.cell.notOffered')} />;
  }
}
